import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Hexagon } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const isCancel = location.pathname.includes("cancel");
  const [status, setStatus] = useState(isCancel ? "cancel" : "checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (isCancel) return;
    const params = new URLSearchParams(location.search);
    const sid = params.get("transaction_id") || params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let cancelled = false;

    const poll = async (n) => {
      if (cancelled) return;
      if (n > 10) { setStatus("timeout"); return; }
      try {
        const res = await api.get(`/payments/status/${sid}`);
        if (res.data.payment_status === "paid") { setStatus("success"); return; }
        if (["expired", "failed"].includes(res.data.payment_status)) { setStatus("error"); return; }
      } catch (e) {
        console.error("Payment status poll failed", e);
      }
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { cancelled = true; };
  }, [isCancel, location.search]);

  const CONTENT = {
    checking: { icon: Loader2, spin: true, title: "Confirming your payment…", desc: `Verifying with Stripe (attempt ${attempts + 1})`, color: "text-emerald-400" },
    success: { icon: CheckCircle2, title: "Payment successful", desc: "Your subscription is active. Welcome aboard!", color: "text-emerald-400" },
    cancel: { icon: XCircle, title: "Checkout cancelled", desc: "No charge was made. You can subscribe anytime.", color: "text-amber-400" },
    error: { icon: XCircle, title: "Payment issue", desc: "We couldn't confirm the payment. Try again or contact support.", color: "text-red-400" },
    timeout: { icon: XCircle, title: "Still processing", desc: "Payment is taking longer than usual. Check your billing shortly.", color: "text-amber-400" },
  };
  const c = CONTENT[status];
  const Icon = c.icon;

  return (
    <div className="min-h-screen bg-background grid-pattern flex items-center justify-center px-4" data-testid="payment-result">
      <div className="glass rounded-2xl border border-slate-800 p-10 max-w-md w-full text-center shadow-2xl shadow-black/50">
        <Hexagon className="w-7 h-7 text-emerald-400 mx-auto mb-6" />
        <Icon className={`w-14 h-14 mx-auto ${c.color} ${c.spin ? "animate-spin" : ""}`} />
        <h1 className="mt-5 font-display text-2xl font-bold text-white" data-testid="payment-title">{c.title}</h1>
        <p className="mt-2 text-slate-400 text-sm">{c.desc}</p>
        {status !== "checking" && (
          <Button onClick={() => navigate("/workspace")} data-testid="payment-continue-btn"
            className="mt-7 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-11 px-7">
            Go to Workspace
          </Button>
        )}
      </div>
    </div>
  );
}
